// src/whatsappChat/components/PromptEditor.jsx
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faSpinner, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { useWaPrompts, useWaSavePrompts } from '../api/hooks';
import { normalizeError } from '../api/client';
import { showToast } from '../../components/toast.jsx';
import { Field } from './Modal';

const COPY = {
  system: {
    title: 'System Prompt',
    hint: 'Sets how the assistant behaves when it drafts suggested replies.',
  },
  user: {
    title: 'User Prompt',
    hint: 'Added to every suggested-reply request, on top of the system prompt.',
  },
};

/** One prompt, edited in place on the Setup page. `type` is 'system' or 'user'. */
export function PromptEditor({ type }) {
  const { data: prompts, isLoading, isError, error } = useWaPrompts();
  const savePrompts = useWaSavePrompts();

  const isUserPrompt = type === 'user';
  const { title, hint } = COPY[isUserPrompt ? 'user' : 'system'];
  const saved = (isUserPrompt ? prompts?.user_prompt : prompts?.system_prompt) || '';

  const [text, setText] = useState('');
  const loaded = useRef(false);

  // Filled once, so a later refetch never overwrites what is being typed.
  useEffect(() => {
    if (prompts && !loaded.current) {
      loaded.current = true;
      setText(saved);
    }
  }, [prompts, saved]);

  const isBusy = savePrompts.isPending;
  const changed = text.trim() !== saved.trim();

  const handleSave = async (e) => {
    e?.preventDefault();
    if (isBusy || !changed) return;
    const value = text.trim();
    try {
      // `system_propmt` is the key the API has always been sent alongside the correct one.
      const payload = isUserPrompt ? { user_prompt: value } : { system_prompt: value, system_propmt: value };
      await savePrompts.mutateAsync(payload);
      setText(value);
      showToast.success(`${title} saved successfully`);
    } catch (err) {
      showToast.error(normalizeError(err).message || 'Failed to save prompt');
    }
  };

  return (
    <form className="wa-card wa-card-pad wa-setup-prompt" onSubmit={handleSave}>
      <h2 className="wa-card-title">{title}</h2>

      {isLoading ? (
        <div className="wa-state">Loading prompt…</div>
      ) : isError ? (
        <div className="wa-state">{normalizeError(error).message}</div>
      ) : (
        <>
          <Field label={`${title} Text`} hint={hint}>
            <textarea
              className="wa-input wa-setup-prompt-input"
              rows={10}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Enter ${title.toLowerCase()} here...`}
              disabled={isBusy}
            />
          </Field>

          <div className="wa-setup-prompt-foot">
            <span className="wa-muted">
              {text.length} characters · {text.trim() ? text.trim().split(/\s+/).length : 0} words
            </span>
            <div className="wa-setup-actions">
              <button
                type="button"
                className="wa-btn wa-btn-secondary"
                onClick={() => setText(saved)}
                disabled={isBusy || !changed}
              >
                <FontAwesomeIcon icon={faRotateLeft} /> Reset
              </button>
              <button type="submit" className="wa-btn wa-btn-primary" disabled={isBusy || !changed}>
                <FontAwesomeIcon icon={isBusy ? faSpinner : faFloppyDisk} spin={isBusy} />
                {isBusy ? 'Saving…' : 'Save Prompt'}
              </button>
            </div>
          </div>
        </>
      )}
    </form>
  );
}
